import { ReactNode, useEffect } from "react";
import { PostHogProvider, usePostHog } from "@posthog/react";
import posthogJs, { type PostHog, type PostHogConfig } from "posthog-js";

import { toPhone } from "common/utils/formatting";
import { Optional } from "common/utils/types";

export type PostHogContext = Record<string, string | number | boolean | null>;

const postHogOptions: Partial<PostHogConfig> = {
  autocapture: false,
  capture_pageview: false,
  disable_session_recording: true,
};

/** Base properties supplied by ClientBundle._get_initial_user_data */
export type PostHogAnalyticsUser = {
  id: number;
  email: Optional<string>;
  phone_number: Optional<number>;
  first_name: Optional<string>;
  last_name: Optional<string>;
};

export type PostHogAnalyticsIdentity = {
  uniqueId: string;
  email: Optional<string>;
  phoneNumber: Optional<number>;
  firstName: Optional<string>;
  lastName: Optional<string>;
};

/** A PostHog group to associate all captured events with. */
export type PostHogAnalyticsGroup = {
  type: string;
  key: string;
  properties?: Record<string, unknown>;
};

type PostHogAnalyticsProviderProps = {
  children: ReactNode;
  isEnabled: boolean;
  identity: PostHogAnalyticsIdentity | null;
  groups: PostHogAnalyticsGroup[];
  context: PostHogContext;
};

type PostHogSessionProps = {
  identity: PostHogAnalyticsIdentity | null;
  groups: PostHogAnalyticsGroup[];
  context: PostHogContext;
};

const PostHogSession = ({ identity, groups, context }: PostHogSessionProps) => {
  const posthog = usePostHog();

  useEffect(() => {
    posthog.register(context);
  }, [posthog, context]);

  // reset() clears group associations, so groups are re-applied after every identify/reset.
  useEffect(() => {
    if (identity) {
      posthog.identify(identity.uniqueId, {
        email: identity.email,
        phone_number: toPhone(identity.phoneNumber),
        first_name: identity.firstName,
        last_name: identity.lastName,
      });
    } else {
      posthog.reset();
    }
    groups.forEach(({ type, key, properties }) => {
      posthog.group(type, key, properties);
    });
  }, [identity, groups, posthog]);

  return null;
};

let postHogClient: PostHog | null = null;

/**
 * Returns the singleton PostHog client, initializing it on first use.
 *
 * We own initialization (rather than letting PostHogProvider do it from an
 * effect) so the client is ready before PostHogSession's effects run identify,
 * register, and group.
 */
const getPostHogClient = (): PostHog => {
  if (!postHogClient) {
    posthogJs.init(window.POSTHOG_PUBLIC_API_KEY, postHogOptions);
    postHogClient = posthogJs;
  }
  return postHogClient;
};

const PostHogAnalyticsProvider = ({
  children,
  identity,
  groups,
  isEnabled,
  context,
}: PostHogAnalyticsProviderProps) => {
  if (!isEnabled || !window.POSTHOG_PUBLIC_API_KEY) {
    return <>{children}</>;
  }

  return (
    <PostHogProvider client={getPostHogClient()}>
      <PostHogSession identity={identity} groups={groups} context={context} />
      {children}
    </PostHogProvider>
  );
};

export default PostHogAnalyticsProvider;
